/**
 * JSONHistory - Manages localStorage-based JSON history
 * Stores multiple JSON entries with timestamps and metadata
 */
export class JSONHistory {
  constructor(options = {}) {
    this.options = {
      storageKey: 'json-viewer-history',
      maxEntries: 50, // Maximum number of history entries to keep
      maxSize: 1024 * 1024, // 1MB max total storage size
      ...options
    }
  }

  /**
   * Add a new JSON entry to history
   * @param {string} content - The JSON content
   * @param {Object} metadata - Additional metadata
   * @returns {string} - The ID of the added entry
   */
  addEntry(content, metadata = {}) {
    if (!content || !content.trim()) {
      return null
    }

    try {
      const history = this.getHistory()
      const now = Date.now()
      const id = this.generateId(now)

      // Check if this exact content already exists (avoid duplicates)
      const existingEntry = history.find(entry => entry.content === content)
      if (existingEntry) {
        // Update timestamp and move to front
        existingEntry.timestamp = now
        existingEntry.lastAccessed = now
        this.saveHistory(this.sortByTimestamp(history))
        return existingEntry.id
      }

      const entry = {
        id,
        content: content.trim(),
        timestamp: now,
        lastAccessed: now,
        size: content.length,
        preview: this.generatePreview(content),
        isValid: this.validateJSON(content),
        ...metadata
      }

      // Add to beginning of array (most recent first)
      history.unshift(entry)

      // Trim history if needed
      const trimmedHistory = this.trimHistory(history)

      this.saveHistory(trimmedHistory)
      return id
    } catch (error) {
      console.warn('Failed to add entry to JSON history:', error)
      return null
    }
  }

  /**
   * Get all history entries
   * @returns {Array} Array of history entries
   */
  getHistory() {
    try {
      const stored = localStorage.getItem(this.options.storageKey)
      if (stored) {
        const history = JSON.parse(stored)
        return Array.isArray(history) ? history : []
      }
    } catch (error) {
      console.warn('Failed to load JSON history:', error)
    }
    return []
  }

  /**
   * Get a specific entry by ID
   * @param {string} id - Entry ID
   * @returns {Object|null} The entry or null if not found
   */
  getEntry(id) {
    const history = this.getHistory()
    const entry = history.find(entry => entry.id === id)

    if (entry) {
      // Update last accessed time
      entry.lastAccessed = Date.now()
      this.saveHistory(history)
    }

    return entry || null
  }

  /**
   * Delete a specific entry
   * @param {string} id - Entry ID to delete
   * @returns {boolean} Success status
   */
  deleteEntry(id) {
    try {
      const history = this.getHistory()
      const filteredHistory = history.filter(entry => entry.id !== id)

      if (filteredHistory.length !== history.length) {
        this.saveHistory(filteredHistory)
        return true
      }
    } catch (error) {
      console.warn('Failed to delete entry from JSON history:', error)
    }
    return false
  }

  /**
   * Clear all history
   * @returns {boolean} Success status
   */
  clearHistory() {
    try {
      localStorage.removeItem(this.options.storageKey)
      return true
    } catch (error) {
      console.warn('Failed to clear JSON history:', error)
      return false
    }
  }

  /**
   * Get recent entries (last N entries)
   * @param {number} count - Number of entries to return
   * @returns {Array} Recent entries
   */
  getRecentEntries(count = 10) {
    const history = this.getHistory()
    return history.slice(0, count)
  }

  /**
   * Search history entries
   * @param {string} query - Search query
   * @returns {Array} Matching entries
   */
  searchHistory(query) {
    if (!query || !query.trim()) {
      return this.getHistory()
    }

    const history = this.getHistory()
    const searchTerm = query.toLowerCase()

    return history.filter(entry =>
      entry.content.toLowerCase().includes(searchTerm) ||
      entry.preview.toLowerCase().includes(searchTerm)
    )
  }

  /**
   * Get history statistics
   * @returns {Object} Statistics about the history
   */
  getStats() {
    const history = this.getHistory()
    const totalSize = history.reduce((sum, entry) => sum + entry.size, 0)
    const validEntries = history.filter(entry => entry.isValid).length

    return {
      totalEntries: history.length,
      validEntries,
      invalidEntries: history.length - validEntries,
      totalSize,
      oldestEntry: history.length > 0 ? Math.min(...history.map(e => e.timestamp)) : null,
      newestEntry: history.length > 0 ? Math.max(...history.map(e => e.timestamp)) : null
    }
  }

  /**
   * Export history as JSON
   * @returns {string} JSON string of history
   */
  exportHistory() {
    const history = this.getHistory()
    return JSON.stringify(history, null, 2)
  }

  /**
   * Import history from JSON
   * @param {string} jsonData - JSON string to import
   * @returns {boolean} Success status
   */
  importHistory(jsonData) {
    try {
      const importedHistory = JSON.parse(jsonData)
      if (!Array.isArray(importedHistory)) {
        throw new Error('Invalid history format')
      }

      const currentHistory = this.getHistory()
      const mergedHistory = [...importedHistory, ...currentHistory]

      // Remove duplicates and trim
      const uniqueHistory = this.removeDuplicates(mergedHistory)
      const trimmedHistory = this.trimHistory(uniqueHistory)

      this.saveHistory(trimmedHistory)
      return true
    } catch (error) {
      console.warn('Failed to import JSON history:', error)
      return false
    }
  }

  // Private methods

  /**
   * Generate a unique ID for an entry
   * @param {number} timestamp - Timestamp
   * @returns {string} Unique ID
   */
  generateId(timestamp) {
    return `json_${timestamp}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Generate a preview of JSON content
   * @param {string} content - JSON content
   * @returns {string} Preview string
   */
  generatePreview(content) {
    try {
      const parsed = JSON.parse(content)
      const keys = Object.keys(parsed)

      if (Array.isArray(parsed)) {
        return `Array[${parsed.length}]`
      } else if (typeof parsed === 'object' && parsed !== null) {
        const keyPreview = keys.slice(0, 3).join(', ')
        const moreKeys = keys.length > 3 ? `, +${keys.length - 3} more` : ''
        return `{${keyPreview}${moreKeys}}`
      } else {
        return String(parsed).substring(0, 50)
      }
    } catch (error) {
      // Return first 50 characters for invalid JSON
      return content.substring(0, 50) + (content.length > 50 ? '...' : '')
    }
  }

  /**
   * Validate JSON content
   * @param {string} content - JSON content
   * @returns {boolean} Is valid JSON
   */
  validateJSON(content) {
    try {
      JSON.parse(content)
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Sort history by timestamp (newest first)
   * @param {Array} history - History array
   * @returns {Array} Sorted history
   */
  sortByTimestamp(history) {
    return history.sort((a, b) => b.timestamp - a.timestamp)
  }

  /**
   * Remove duplicate entries based on content
   * @param {Array} history - History array
   * @returns {Array} History without duplicates
   */
  removeDuplicates(history) {
    const seen = new Set()
    return history.filter(entry => {
      if (seen.has(entry.content)) {
        return false
      }
      seen.add(entry.content)
      return true
    })
  }

  /**
   * Trim history to fit size and count limits
   * @param {Array} history - History array
   * @returns {Array} Trimmed history
   */
  trimHistory(history) {
    let trimmedHistory = [...history]

    // Sort by timestamp first (newest first)
    trimmedHistory = this.sortByTimestamp(trimmedHistory)

    // Trim by count
    if (trimmedHistory.length > this.options.maxEntries) {
      trimmedHistory = trimmedHistory.slice(0, this.options.maxEntries)
    }

    // Trim by size
    let totalSize = 0
    const sizeLimitedHistory = []

    for (const entry of trimmedHistory) {
      if (totalSize + entry.size <= this.options.maxSize) {
        sizeLimitedHistory.push(entry)
        totalSize += entry.size
      } else {
        break
      }
    }

    return sizeLimitedHistory
  }

  /**
   * Save history to localStorage
   * @param {Array} history - History array to save
   */
  saveHistory(history) {
    try {
      localStorage.setItem(this.options.storageKey, JSON.stringify(history))
    } catch (error) {
      console.warn('Failed to save JSON history:', error)
    }
  }
}
